import { useChatStoreActions } from "PFApp/stores/chat_store";
import { Button } from "PFComponents/button";
import { Inline, Stack } from "PFComponents/containers";
import { Divider } from "PFComponents/divider";
import { Pill } from "PFComponents/pill";
import { Typography } from "PFComponents/typography";
import { isMsTeams } from "PFCore/helpers/ms_teams";
import { getProfileName } from "PFCore/helpers/profile";
import { Conversation } from "PFCore/types";
import { ChatParticipant } from "PFCore/types/chat_participant";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./activity_overview.module.scss";

type ActivityOverviewChatItemProps = {
  conversation: Conversation;
  participants: ChatParticipant[];
  isLast?: boolean;
};

export const ActivityOverviewChatItem = ({
  conversation,
  participants,
  isLast
}: ActivityOverviewChatItemProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "show.details.chats" });
  const { renderChat } = useChatStoreActions();
  const [isUnread, setIsUnread] = useState(!!conversation.unreadCount);

  const chatName = useMemo(() => {
    if (conversation.kind === "primary") {
      return t("activityChat");
    }

    if (!participants || participants.length === 0) {
      return t("loading");
    }

    const firstUser = getProfileName(participants[0].profile);

    if (participants.length === 1) {
      return firstUser;
    }

    if (participants.length === 2) {
      const secondUser = getProfileName(participants[1].profile);
      return t("twoParticipants", { firstUser, secondUser });
    }

    const count = participants.length - 1;
    return t("multipleParticipants", { firstUser, count });
  }, [conversation.kind, participants, t]);

  const handleOpenConversation = () => {
    if (isMsTeams()) {
      window.open(window.location.href);
      return;
    }

    renderChat(conversation.id);
    setIsUnread(false);
  };

  return (
    <Stack>
      <Inline spread alignItems="center">
        <Inline alignItems="center">
          <Inline className={css.chatItemName} alignItems="center">
            <Typography variant="bodyBold" clipOverflow>
              {chatName}
            </Typography>
            {isUnread && <Pill text={t("unread")} small />}
          </Inline>
          <Typography variant="labelRegular">{t("participants", { count: participants.length })}</Typography>
        </Inline>
        <Button
          qaId="ActivityDetailsChat-JoinIn"
          small
          kind="secondary"
          text={t("joinIn")}
          onClick={handleOpenConversation}
        />
      </Inline>
      {!isLast && <Divider />}
    </Stack>
  );
};
